import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, MessageCircle } from "lucide-react";
import { COMPANY, PRODUCTS } from "@/data/products";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid="site-footer"
      className="border-t border-[var(--eel-border)] bg-[var(--eel-surface)] mt-24"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="flex items-center gap-3">
            <img
              src={COMPANY.logoUrl}
              alt="EELLEVEN MINING"
              className="w-12 h-12 rounded-full object-cover ring-1 ring-zinc-700"
            />
            <div>
              <span className="font-heading font-black tracking-tighter uppercase block">
                EELLEVEN <span className="copper-text">Mining</span>
              </span>
              <span className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                {COMPANY.tagline}
              </span>
            </div>
          </div>
          <p className="mt-5 text-sm text-zinc-400 max-w-md leading-relaxed">
            Direct supply of copper cathodes, copper wire scrap,
            copper concentrate, copper ore, cobalt ore,
            manganese ore and coal — sourced from Zambia and the
            wider Central African region.
          </p>
        </div>

        <div className="md:col-span-3">
          <h4 className="label-eyebrow">Products</h4>
          <ul className="mt-4 space-y-2 text-sm">
            {PRODUCTS.slice(0, 6).map((p) => (
              <li key={p.slug}>
                <Link
                  to={`/products#${p.slug}`}
                  className="text-zinc-300 hover:text-[var(--eel-copper)] transition-colors"
                  data-testid={`footer-product-${p.slug}`}
                >
                  {p.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-2">
          <h4 className="label-eyebrow">Navigate</h4>
          <ul className="mt-4 space-y-2 text-sm">
            {[
              ["About", "/about"],
              ["Products", "/products"],
              ["Invest", "/invest"],
              ["Mine For Sale", "/mine-for-sale"],
              ["Careers", "/careers"],
              ["Gallery", "/gallery"],
              ["Contact", "/contact"],
            ].map(([label, href]) => (
              <li key={label}>
                <Link
                  to={href}
                  className="text-zinc-300 hover:text-[var(--eel-copper)] transition-colors"
                  data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-3">
          <h4 className="label-eyebrow">Contact</h4>
          <ul className="mt-4 space-y-3 text-sm text-zinc-300">
            <li className="flex items-start gap-2">
              <MapPin size={14} className="mt-0.5 copper-text" />
              <span>
                {COMPANY.address}
                <br />
                {COMPANY.country}
              </span>
            </li>
            <li className="flex items-start gap-2">
              <Mail size={14} className="mt-0.5 copper-text" />
              <a
                href={`mailto:${COMPANY.email}`}
                className="hover:text-white break-all"
                data-testid="footer-email"
              >
                {COMPANY.email}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <MessageCircle size={14} className="mt-0.5 text-green-500" />
              <a
                href={`https://wa.me/${COMPANY.whatsappPrimaryDigits}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white"
                data-testid="footer-wa-ireland"
              >
                WhatsApp · {COMPANY.whatsappPrimary}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <Phone size={14} className="mt-0.5 copper-text" />
              <a
                href={`tel:${COMPANY.phoneZambia}`}
                className="hover:text-white"
                data-testid="footer-phone-zm"
              >
                {COMPANY.phoneZambia}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <MessageCircle size={14} className="mt-0.5 text-green-500" />
              <a
                href={`https://wa.me/${COMPANY.phoneZambia2Digits}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white"
                data-testid="footer-phone-zm2"
              >
                WhatsApp · {COMPANY.phoneZambia2}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-zinc-500">
          <p data-testid="footer-copy">
            © {year} {COMPANY.name}. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <Link
              to="/admin"
              data-testid="footer-admin-link"
              className="hover:text-[var(--eel-copper)] uppercase tracking-widest"
            >
              Admin
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
